<template>
  <div class="modal fade" :class="show ? 'show' : 'hide'" style="display: block; background-color: rgba(0,0,0,0.3)">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="search-modal-header">
          <font-awesome-icon class="warning-icon" icon="exclamation-triangle"/>
          <span class="search-modal-title">Following action will clear your filters and results</span>
          <button class="close" type="button" @click="closeWithoutSearch"><span aria-hidden="true">×</span></button>
        </div>
        <div class="search-modal-body">
          <span v-if="!shouldSaveSearch">Would you like to save your current search criteria?</span>
          <div style="position: relative" v-else>
            <span>Please, insert a name for your search:</span>
            <el-input v-model="searchName" :class="{'is-invalid': localSaveMessage.error}"
                      style="height: 40px; width: 350px" placeholder="Enter search name" clearable
                      @input="resetSaveMessage">
              <template #append>
                <div @click.prevent="() => save(searchName, filters)"><font-awesome-icon style="font-size: 37px; margin: 0 5px;" icon="cloud-upload-alt" color="white"></font-awesome-icon> </div>
              </template>
            </el-input>
          </div>
        </div>
        <div class="search-modal-footer">
          <button class="btn light-button" @click="close" type="button">{{shouldSaveSearch ? "Cancel" : "No"}}</button>
          <button v-if="!shouldSaveSearch" class="btn dark-button" type="button" @click="shouldSaveSearch = true">Yes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import studiesActions from '../../store/modules/studies/actionTypes'

export default {
  name: 'SearchPopup',
  emits: ['update:saveMessage'],
  props: {
    search: Function,
    saveSearch: Function,
    clearData: Function,
    saveMessage: Object,
    filters: Object
  },
  data () {
    return {
      show: false,
      shouldSaveSearch: false,
      searchName: '',
      nextLocation: null,
      localSaveMessage: { ...this.saveMessage }
    }
  },
  methods: {
    ...mapActions({
      updateStoreFilters: studiesActions.SET_OR_CLEAR_SEARCH_FILTERS,
      updateCICSFilters: studiesActions.RESET_CICS_FILTERS
    }),
    showPopup (nextLocation) {
      if (nextLocation) {
        this.nextLocation = nextLocation
      }
      this.show = true
    },
    closePopup () {
      this.show = false
      this.shouldSaveSearch = false
    },
    resetSaveMessage () {
      this.localSaveMessage = {
        ...this.localSaveMessage,
        error: false,
        value: ''
      }
    },
    async save (name) {
      const status = await this.saveSearch(name, this.filters)
      if (status) {
        if (this.nextLocation) {
          if (this.$route.name === 'cics') {
            this.updateCICSFilters()
          } else {
            this.updateStoreFilters({ clearFilters: true, filters: {} })
          }
          this.$router.push({ name: this.nextLocation, query: { allowLocationChange: '1' } })
        } else {
          this.search()
        }
      }
      this.closePopup()
    },
    closeWithoutSearch () {
      this.closePopup()
    },
    close () {
      if (this.nextLocation) {
        if (this.$route.name === 'cics') {
          this.updateCICSFilters()
        } else {
          this.updateStoreFilters({ clearFilters: true, filters: {} })
          this.clearData()
        }
        this.$router.push({ name: this.nextLocation, query: { allowLocationChange: '1' } })
      } else {
        this.search()
      }
      this.closePopup()
    }
  },
  watch: {
    // These two keep the prop and the local copy in step. The guards have to
    // compare by value: every branch here assigns a freshly spread object, so
    // a `!==` identity check can never be false and the pair never settles.
    // Each pass allocated two more objects and re-triggered the other watcher,
    // which locked the tab up rather than raising anything. SearchFilters gets
    // this right; this component did not.
    saveMessage: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.localSaveMessage)) {
          this.localSaveMessage = _.cloneDeep(value)
        }
      }
    },
    localSaveMessage: {
      deep: true,
      handler (value) {
        if (!_.isEqual(value, this.saveMessage)) {
          this.$emit('update:saveMessage', _.cloneDeep(value))
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/scss/variables";

.hide {
  height: 0;
}

.show {
  height: 100%;
}

.modal-dialog {
  margin-top: 80px;
}

.modal-content {
  width: 604px;
  min-height: 215px;
  background-color: #FFFAF3 !important;
  border: 1px solid #CCBEA0 !important;
  border-radius: 4px;

  .search-modal-header {
    display: flex;
    justify-content: flex-start;
    height: 42px;
    width: 100%;
    margin-top: 33px;
    align-items: flex-end;
    padding-left: 42px;

    .search-modal-title {
      color: #7A4D05;
      font-family: 'Source Sans Pro', sans-serif;
      font-size: 18px;
      font-weight: 900;
      line-height: 23px;
      letter-spacing: 0;
      margin-left: 41px;
    }

    .close {
      position: absolute;
      top: 11px;
      right: 11px;
      line-height: 0.8125rem;
    }

    .warning-icon {
      font-size: 40px;
      color: $charcoal;
    }
  }

  .search-modal-body {
    margin-top: 19px;
    margin-left: 129px;
    span {
      color: #7A4D05CC;
      font-family: 'Source Sans Pro', sans-serif;
      font-size: 14px;
      line-height: 24px;
      letter-spacing: 0;
    }

    .error-message {
      position: absolute;
      color: #f86c6b;
      font-size: 12px;
    }

    .disable-save {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      background-color: #4f6ec0 !important;
      padding: 1px 8px;
      height: 100%;
    }
  }

  .search-modal-footer {
    margin-top: 38px;
    display: flex;
    justify-content: flex-end;
    margin-right: 47px;
    margin-bottom: 10px;

    .btn {
      width: 73px;
      height: 40px;
      border-radius: 6px;
      color: white;
      font-family: 'Source Sans Pro', sans-serif;
      font-size: 11px;
      line-height: 20px;
      letter-spacing: 0;
      font-weight: 900;
    }

    .light-button {
      background-color: $silver;
      transition: background-color 0.2s linear;
      &:hover {
        background-color: $pewter;
      }
    }

    .dark-button {
      background-color: $pewter;
      margin-left: 32px;
      transition: background-color 0.2s linear;
      &:hover {
        background-color: $charcoal;
      }
    }
  }
}

</style>
<style lang="scss">
@import "../../assets/scss/variables";

.search-modal-body {
  .el-input__inner {
    border-right: 0 !important;
  }

  .el-input-group__append {
    color: white !important;
    cursor: pointer;
    padding: 0 !important;
    border: 0 !important;

    div {
      background-color: $active-link;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      padding: 1px 8px;
      height: 100%;
    }
  }
}
</style>
